// src/components/menu/menu.js - Versão final com subitens animados

import React, { useState, useEffect, useRef } from "react";
import LogoImage from "../../assets/img/logo.png";
import Status from "../status/status";
import { getMenuButtons, imagePages } from "../../configs/menuConfig";
import alameSound from "../../assets/sound/alarmeSound.mp3";
import "../../styles/desktop/menu.css";

// Lista de páginas que são consideradas alarmes
const ALARM_PAGES = ["CaixaDAgua", "Bombas", "CentralAlarmeIncendio"];

// Submenu item
const SubmenuItem = ({
  item,
  isActive,
  onClick,
  onImageChange,
  paginas = [],
  alarmStatus = null,
  tipoAlarmeAtivo = null
}) => {
  const {
    id,
    label,
    page,
    icon: Icon,
    iconProps = {},
    isLocked,
    lockIcon: LockIcon,
    requiresImage,
    imageIndex
  } = item;

  const handleClick = () => {
    if (!isLocked) {
      if (requiresImage && paginas[imageIndex]) {
        onImageChange(paginas[imageIndex]);
      }
      onClick(page);
    }
  };

  const renderIcon = () => {
    if (!Icon) return null;
    const finalProps = iconProps.getDynamicProps
      ? { ...iconProps, ...iconProps.getDynamicProps(isActive) }
      : iconProps;
    return <Icon {...finalProps} />;
  };

  // ✨ Verifica se ESTE item específico deve piscar
  // Só pisca se há alarme E se este item corresponde ao tipo de alarme ativo
  const isAlarmPage = ALARM_PAGES.includes(page);
  const isThisItemsAlarm = page === tipoAlarmeAtivo; // Só este item específico
  const shouldShowAlarm = isAlarmPage && alarmStatus && isThisItemsAlarm;

  const getButtonClasses = () => {
    const classes = ["btn"];
    
    if (shouldShowAlarm) {
      classes.push("activoAlert");
    } else if (isActive) {
      classes.push("active");
    }
    
    if (isLocked) {
      classes.push("bloqueado");
    }
    
    return classes.filter(Boolean).join(" ");
  };

  return (
    <button
      key={id}
      className={getButtonClasses()}
      onClick={handleClick}
      disabled={isLocked}
    >
      {label}
      {renderIcon()}
      {isLocked && LockIcon && (
        <span className="icon-cadeado">
          <LockIcon width="25" height="25" />
        </span>
      )}
    </button>
  );
};

// Menu button
const MenuButton = ({
  button,
  isActive,
  onClick,
  onImageChange,
  paginas = [],
  expandedMenus,
  toggleMenu,
  closeAllMenus,
  alarmStatus = null,
  paginaAtual,
  tipoAlarmeAtivo = null
}) => {
  const {
    id,
    label,
    page,
    icon: Icon,
    iconProps = {},
    customIcon,
    isLocked,
    lockIcon: LockIcon,
    className = "",
    hasSubmenu,
    submenuItems = [],
    hasAlarmIndicator
  } = button;

  const isExpanded = expandedMenus[id];

  const handleClick = () => {
    if (hasSubmenu) {
      toggleMenu(id);
      return;
    }
    closeAllMenus();
    if (!isLocked) {
      if (imagePages.requiresImageClear.includes(page)) {
        onImageChange("");
      }
      
      // Se é um botão de monitoramento e há alarme ativo, redireciona para tipo específico
      if (hasAlarmIndicator && alarmStatus && tipoAlarmeAtivo) {
        onClick(tipoAlarmeAtivo);
      } else {
        onClick(page);
      }
    }
  };

  const renderIcon = () => {
    if (customIcon) {
      return customIcon(isActive);
    }
    if (Icon) {
      const finalProps = iconProps.getDynamicProps
        ? { ...iconProps, ...iconProps.getDynamicProps(isActive) }
        : iconProps;
      return <Icon {...finalProps} />;
    }
    return null;
  };

  const getButtonClasses = () => {
    const classes = ["btn", className];
    if (hasAlarmIndicator && alarmStatus) {
      classes.push("activoAlert");
    } else if (isActive) {
      classes.push("active");
    }
    if (isLocked) {
      classes.push("bloqueado");
    }
    return classes.filter(Boolean).join(" ");
  };

  return (
    <>
      <button
        key={id}
        className={getButtonClasses()}
        onClick={handleClick}
        disabled={isLocked && !hasSubmenu}
      >
        {label}
        {renderIcon()}
        {isLocked && LockIcon && (
          <span className="icon-cadeado">
            <LockIcon width="25" height="25" />
          </span>
        )}
      </button>

      {hasSubmenu && isExpanded && (
        <div className={`submenu-${id.toLowerCase()} expandido`}>
          {submenuItems.map((item) => {
            const isSubActive = paginaAtual === item.page;
            
            return (
              <SubmenuItem
                key={item.id}
                item={item}
                isActive={isSubActive}
                onClick={(itemPage) => {
                  onClick(itemPage);
                  closeAllMenus();
                }}
                onImageChange={onImageChange}
                paginas={paginas}
                alarmStatus={alarmStatus} // Status do alarme
                tipoAlarmeAtivo={tipoAlarmeAtivo} // ✨ Tipo específico do alarme
              />
            );
          })}
        </div>
      )}
    </>
  );
};

// Main Menu component
export default function Menu({
  trocarPagina,
  trocarImagem,
  paginas = [],
  paginaAtual,
  alarmStatus,
  tipoAlarmeAtivo = null,
  soundEnabled = true
}) {
  const menuButtons = getMenuButtons();
  const [expandedMenus, setExpandedMenus] = useState({});
  const audioRef = useRef(new Audio(alameSound));

  // Configure volume and looping once
  useEffect(() => {
    const audio = audioRef.current;
    audio.volume = 0.4;
    audio.loop = true;
  }, []);

  // Redirect and play/pause alarm sound
  useEffect(() => {
    if (alarmStatus) {
      // ✨ Se há tipo de alarme específico, redireciona para ele
      if (tipoAlarmeAtivo) {
        trocarPagina(tipoAlarmeAtivo);
      } else {
        // Fallback para página genérica de alarme
        trocarPagina("Alarme");
      }
      
      if (soundEnabled) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // autoplay blocked; ignore
          });
        }
      }
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [alarmStatus, tipoAlarmeAtivo, soundEnabled, trocarPagina]); // ✨ Inclui tipoAlarmeAtivo

  const closeAllMenus = () => {
    setExpandedMenus({});
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key !== menuId) next[key] = false;
      });
      next[menuId] = !prev[menuId];
      return next;
    });
  };

  const handleNavigation = (page) => {
    trocarPagina(page);
  };

  return (
    <nav className="menu">
      <div id="barraDeOpções">
        <img src={LogoImage} alt="Logo" id="logo" />
        <div id="barraDeOpções-Botoes">
          {menuButtons.map((button) => (
            <MenuButton
              key={button.id}
              button={button}
              isActive={
                paginaAtual === button.page ||
                button.submenuItems?.some(item => paginaAtual === item.page)
              }
              paginaAtual={paginaAtual}
              onClick={handleNavigation}
              onImageChange={trocarImagem}
              paginas={paginas}
              expandedMenus={expandedMenus}
              toggleMenu={toggleMenu}
              closeAllMenus={closeAllMenus}
              alarmStatus={button.hasAlarmIndicator ? alarmStatus : null}
              tipoAlarmeAtivo={tipoAlarmeAtivo}
            />
          ))}
        </div>
      </div>
      <div className="statusObject">
        <Status />
      </div>
    </nav>
  );
}